//import React, { useState } from "react";
import React, { useState, useEffect, useRef } from "react";
import { toast } from 'react-toastify';

import {
    registerConsumer,
    updateConsumer
} from '../api/consumerService';

const ImageCropperModal = ({ imageSrc, onCrop, onCancel, title = "Crop Image" }) => {
    const containerRef = useRef(null);
    const imageRef = useRef(null);

    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [box, setBox] = useState({ x: 0, y: 0, w: 0, h: 0 });
    const [dragState, setDragState] = useState(null);
    const [currentImageSrc, setCurrentImageSrc] = useState(imageSrc);

    const onImageLoad = (e) => {
        const img = e.target;
        const rect = img.getBoundingClientRect();
        const w = rect.width;
        const h = rect.height;
        setDimensions({ width: w, height: h });
        setBox({
            x: w * 0.1,
            y: h * 0.1,
            w: w * 0.8,
            h: h * 0.8
        });
    };

    const handleStartDrag = (handle, e) => {
        e.preventDefault();
        const clientX = e.clientX || (e.touches && e.touches[0].clientX);
        const clientY = e.clientY || (e.touches && e.touches[0].clientY);
        setDragState({
            handle,
            startX: clientX,
            startY: clientY,
            startBox: { ...box }
        });
    };

    const handleDragMove = (e) => {
        if (!dragState) return;
        const clientX = e.clientX || (e.touches && e.touches[0].clientX);
        const clientY = e.clientY || (e.touches && e.touches[0].clientY);

        const dx = clientX - dragState.startX;
        const dy = clientY - dragState.startY;

        const { handle, startBox } = dragState;
        let nextBox = { ...startBox };
        const minSize = 40;

        switch (handle) {
            case "MOVE":
                nextBox.x = Math.max(0, Math.min(dimensions.width - startBox.w, startBox.x + dx));
                nextBox.y = Math.max(0, Math.min(dimensions.height - startBox.h, startBox.y + dy));
                break;
            case "TL":
                nextBox.x = Math.max(0, Math.min(startBox.x + startBox.w - minSize, startBox.x + dx));
                nextBox.y = Math.max(0, Math.min(startBox.y + startBox.h - minSize, startBox.y + dy));
                nextBox.w = startBox.x + startBox.w - nextBox.x;
                nextBox.h = startBox.y + startBox.h - nextBox.y;
                break;
            case "TR":
                nextBox.y = Math.max(0, Math.min(startBox.y + startBox.h - minSize, startBox.y + dy));
                nextBox.w = Math.max(minSize, Math.min(dimensions.width - startBox.x, startBox.w + dx));
                nextBox.h = startBox.y + startBox.h - nextBox.y;
                break;
            case "BL":
                nextBox.x = Math.max(0, Math.min(startBox.x + startBox.w - minSize, startBox.x + dx));
                nextBox.w = startBox.x + startBox.w - nextBox.x;
                nextBox.h = Math.max(minSize, Math.min(dimensions.height - startBox.y, startBox.h + dy));
                break;
            case "BR":
                nextBox.w = Math.max(minSize, Math.min(dimensions.width - startBox.x, startBox.w + dx));
                nextBox.h = Math.max(minSize, Math.min(dimensions.height - startBox.y, startBox.h + dy));
                break;
            case "T":
                nextBox.y = Math.max(0, Math.min(startBox.y + startBox.h - minSize, startBox.y + dy));
                nextBox.h = startBox.y + startBox.h - nextBox.y;
                break;
            case "B":
                nextBox.h = Math.max(minSize, Math.min(dimensions.height - startBox.y, startBox.h + dy));
                break;
            case "L":
                nextBox.x = Math.max(0, Math.min(startBox.x + startBox.w - minSize, startBox.x + dx));
                nextBox.w = startBox.x + startBox.w - nextBox.x;
                break;
            case "R":
                nextBox.w = Math.max(minSize, Math.min(dimensions.width - startBox.x, startBox.w + dx));
                break;
            default:
                break;
        }

        setBox(nextBox);
    };

    const handleDragEnd = () => {
        setDragState(null);
    };

    const rotateImage = (clockwise) => {
        const img = imageRef.current;
        if (!img) return;

        const canvas = document.createElement("canvas");
        canvas.width = img.naturalHeight;
        canvas.height = img.naturalWidth;
        const ctx = canvas.getContext("2d");

        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((clockwise ? 90 : -90) * Math.PI / 180);
        ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);

        const rotatedDataUrl = canvas.toDataURL("image/jpeg", 0.95);
        setCurrentImageSrc(rotatedDataUrl);
    };

    const handleCrop = () => {
        const img = imageRef.current;
        if (!img || dimensions.width === 0) return;

        const scaleX = img.naturalWidth / dimensions.width;
        const scaleY = img.naturalHeight / dimensions.height;

        const cropX = box.x * scaleX;
        const cropY = box.y * scaleY;
        const cropW = box.w * scaleX;
        const cropH = box.h * scaleY;

        const canvas = document.createElement("canvas");
        canvas.width = cropW;
        canvas.height = cropH;
        const ctx = canvas.getContext("2d");

        ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

        canvas.toBlob((blob) => {
            if (blob) {
                onCrop(blob);
            }
        }, "image/jpeg", 0.9);
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 select-none"
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
        >
            <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl space-y-6">
                <div className="flex justify-between items-center border-b pb-3">
                    <h3 className="text-lg font-bold text-gray-800">{title}</h3>
                    <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600 transition text-xl">&times;</button>
                </div>

                <div className="flex justify-center bg-slate-100 rounded-xl p-4 overflow-hidden max-h-[450px]">
                    <div
                        ref={containerRef}
                        className="relative max-w-full max-h-[400px] overflow-hidden select-none"
                    >
                        <img
                            ref={imageRef}
                            src={currentImageSrc}
                            alt="Crop target"
                            onLoad={onImageLoad}
                            draggable={false}
                            className="max-w-full max-h-[400px] object-contain pointer-events-none"
                        />

                        {dimensions.width > 0 && (
                            <>
                                <div
                                    className="absolute border-2 border-dashed border-blue-500 cursor-move"
                                    style={{
                                        left: `${box.x}px`,
                                        top: `${box.y}px`,
                                        width: `${box.w}px`,
                                        height: `${box.h}px`,
                                        boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)"
                                    }}
                                    onMouseDown={(e) => handleStartDrag("MOVE", e)}
                                    onTouchStart={(e) => handleStartDrag("MOVE", e)}
                                >
                                    <div
                                        className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-blue-600 border border-white rounded-full cursor-nwse-resize shadow"
                                        onMouseDown={(e) => { e.stopPropagation(); handleStartDrag("TL", e); }}
                                        onTouchStart={(e) => { e.stopPropagation(); handleStartDrag("TL", e); }}
                                    />
                                    <div
                                        className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-blue-600 border border-white rounded-full cursor-nesw-resize shadow"
                                        onMouseDown={(e) => { e.stopPropagation(); handleStartDrag("TR", e); }}
                                        onTouchStart={(e) => { e.stopPropagation(); handleStartDrag("TR", e); }}
                                    />
                                    <div
                                        className="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-blue-600 border border-white rounded-full cursor-nesw-resize shadow"
                                        onMouseDown={(e) => { e.stopPropagation(); handleStartDrag("BL", e); }}
                                        onTouchStart={(e) => { e.stopPropagation(); handleStartDrag("BL", e); }}
                                    />
                                    <div
                                        className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-blue-600 border border-white rounded-full cursor-nwse-resize shadow"
                                        onMouseDown={(e) => { e.stopPropagation(); handleStartDrag("BR", e); }}
                                        onTouchStart={(e) => { e.stopPropagation(); handleStartDrag("BR", e); }}
                                    />

                                    <div
                                        className="absolute top-0 bottom-0 -left-1 w-2 cursor-ew-resize"
                                        onMouseDown={(e) => { e.stopPropagation(); handleStartDrag("L", e); }}
                                        onTouchStart={(e) => { e.stopPropagation(); handleStartDrag("L", e); }}
                                    />
                                    <div
                                        className="absolute top-0 bottom-0 -right-1 w-2 cursor-ew-resize"
                                        onMouseDown={(e) => { e.stopPropagation(); handleStartDrag("R", e); }}
                                        onTouchStart={(e) => { e.stopPropagation(); handleStartDrag("R", e); }}
                                    />
                                    <div
                                        className="absolute left-0 right-0 -top-1 h-2 cursor-ns-resize"
                                        onMouseDown={(e) => { e.stopPropagation(); handleStartDrag("T", e); }}
                                        onTouchStart={(e) => { e.stopPropagation(); handleStartDrag("T", e); }}
                                    />
                                    <div
                                        className="absolute left-0 right-0 -bottom-1 h-2 cursor-ns-resize"
                                        onMouseDown={(e) => { e.stopPropagation(); handleStartDrag("B", e); }}
                                        onTouchStart={(e) => { e.stopPropagation(); handleStartDrag("B", e); }}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="flex justify-between items-center bg-slate-50 p-3 rounded-xl border border-slate-100">
                    <span className="text-xs font-semibold text-gray-500">Rotate Image</span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => rotateImage(false)}
                            className="bg-white hover:bg-gray-150 text-gray-700 px-3 py-1.5 rounded-lg border text-xs font-semibold shadow-sm transition"
                        >
                            ↺ Rotate Left
                        </button>
                        <button
                            type="button"
                            onClick={() => rotateImage(true)}
                            className="bg-white hover:bg-gray-150 text-gray-700 px-3 py-1.5 rounded-lg border text-xs font-semibold shadow-sm transition"
                        >
                            Rotate Right ↻
                        </button>
                    </div>
                </div>

                <div className="text-center text-xs text-gray-500 font-medium">
                    💡 Drag the corners or edges to adjust the crop box. Drag the middle to move it.
                </div>

                <div className="flex gap-3 justify-end pt-3 border-t">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-xl text-sm font-semibold transition"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleCrop}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-xl text-sm font-bold transition shadow-md shadow-blue-500/20"
                    >
                        Crop & Save
                    </button>
                </div>
            </div>
        </div>
    );
};

const initialForm = {
    Name: "",
    Email: "",
    Mobile: "",
    ConsumerNumber: "",
    Address: "",
    SanctionNumber: "",
    SanctionCapacity: "",
    CapacitySolarPV: "",
    ModuleMake: "",
    ModuleCount: "",
    TotalCapacity: "",
    InverterMakeModel: "",
    InverterRating: "",
    TypeOfCharge: "",
    InverterCapacity: "",
    SanctionDate: "",
    ApplicationNumber: "",
    WattagePerModule: "",
    Category: "",
    SrNoOfPVModule: "",
    SignaturePath: "",
    AadharNumber: "",
    AadharCardPath: "",
    InstallationDate: "",
    Division: "",
    District: "",
    AgreementDate: "",
    RecordStatusId: "",
    CreatedBy: "",
    CreatedDate: "",
    ModifiedBy: "",
    ModifiedDate: "",
    TimeStamp: ""
};
const requiredFields = [
    "Name",
    "Email",
    "Mobile",
    "ConsumerNumber",
    "Address"
];


// const FileUpload = ({ label, onChange, preview }) => (
//     <div className="flex flex-col">
//         <label className="text-sm font-medium text-gray-700 mb-1">{label}</label>
//         <input
//             type="file"
//             accept="image/*"
//             onChange={onChange}
//             className="px-4 py-2 rounded-lg border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//         />
//         {preview && (
//             <img
//                 src={preview}
//                 alt="Preview"
//                 className="h-24 w-auto mt-2 border border-gray-300 rounded-lg object-contain"
//             />
//         )}
//     </div>
// );

const SignatureDrawingPad = ({ onSave, onClearActive }) => {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        // Set actual size matching CSS size multiplied by pixel ratio for crisp lines
        canvas.width = rect.width * (window.devicePixelRatio || 1);
        canvas.height = rect.height * (window.devicePixelRatio || 1);

        const ctx = canvas.getContext("2d");
        ctx.scale(window.devicePixelRatio || 1, window.devicePixelRatio || 1);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = "#0F172A"; // Dark slate brush color
        ctx.lineWidth = 3;
    }, []);

    const getCoordinates = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();

        // Use clientX/Y from pointer events directly
        return {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
    };

    const startDrawing = (e) => {
        e.preventDefault();
        const coords = getCoordinates(e);
        if (!coords) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        ctx.beginPath();
        ctx.moveTo(coords.x, coords.y);
        setIsDrawing(true);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        e.preventDefault();

        const coords = getCoordinates(e);
        if (!coords) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        ctx.lineTo(coords.x, coords.y);
        ctx.stroke();
    };

    const stopDrawing = () => {
        if (isDrawing) {
            setIsDrawing(false);
            saveCanvas();
        }
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        onClearActive();
    };

    const saveCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        canvas.toBlob((blob) => {
            if (blob) {
                onSave(blob);
            }
        }, "image/png");
    };

    return (
        <div className="flex flex-col gap-2 w-full">
            <div className="border border-gray-300 rounded-lg overflow-hidden bg-white relative h-36 w-full touch-none shadow-inner">
                <canvas
                    ref={canvasRef}
                    className="w-full h-full cursor-crosshair block"
                    onPointerDown={startDrawing}
                    onPointerMove={draw}
                    onPointerUp={stopDrawing}
                    onPointerLeave={stopDrawing}
                />
            </div>
            <div className="flex gap-2 justify-end">
                <button
                    type="button"
                    onClick={clearCanvas}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded text-xs font-semibold shadow-sm transition"
                >
                    Clear Drawing
                </button>
            </div>
        </div>
    );
};

const ConsumerForm = ({ initialData, onClose }) => {
    const [form, setForm] = useState(initialData || initialForm);
    const [signatureMode, setSignatureMode] = useState("upload"); // "upload" | "draw"
    const [signatureFile, setSignatureFile] = useState(null);
    const [aadharFile, setAadharFile] = useState(null);
    const [aadharFrontFile, setAadharFrontFile] = useState(null);

    const [aadharBackFile, setAadharBackFile] = useState(null);
    const [previewSignature, setPreviewSignature] = useState("");
    const [previewAadhar, setPreviewAadhar] = useState("");
    const [previewAadharFront, setPreviewAadharFront] = useState("");
    const [previewAadharBack, setPreviewAadharBack] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const isEditMode = !!initialData;

    const signaturePasteAreaRef = useRef(null);
    const aadharFrontPasteAreaRef = useRef(null);
    const aadharBackPasteAreaRef = useRef(null);

    const [cropState, setCropState] = useState({
        isOpen: false,
        imageSrc: "",
        title: "",
        onCrop: null
    });

    useEffect(() => {
        const handlePaste = (e) => {
            const items = e.clipboardData?.items;
            if (!items) return;

            for (const item of items) {
                if (item.type.startsWith("image")) {
                    const blob = item.getAsFile();
                    if (!blob) return;

                    const active = document.activeElement;
                    const reader = new FileReader();
                    reader.onload = () => {
                        if (signaturePasteAreaRef.current?.contains(active)) {
                            setCropState({
                                isOpen: true,
                                imageSrc: reader.result,
                                title: "Crop Signature",
                                onCrop: (croppedBlob) => {
                                    const croppedFile = new File([croppedBlob], "signature.jpg", { type: "image/jpeg" });
                                    setSignatureFile(croppedFile);
                                    setPreviewSignature(URL.createObjectURL(croppedBlob));
                                    setErrors(prev => ({ ...prev, signatureFile: '' }));
                                    setCropState({ isOpen: false, imageSrc: "", title: "", onCrop: null });
                                }
                            });
                        } else if (aadharFrontPasteAreaRef.current?.contains(active)) {
                            setCropState({
                                isOpen: true,
                                imageSrc: reader.result,
                                title: "Crop Aadhar Card Front",
                                onCrop: (croppedBlob) => {
                                    const croppedFile = new File([croppedBlob], "aadhar_front.jpg", { type: "image/jpeg" });
                                    setAadharFrontFile(croppedFile);
                                    setPreviewAadharFront(URL.createObjectURL(croppedBlob));
                                    setErrors(prev => ({ ...prev, aadharFile: '' }));
                                    setCropState({ isOpen: false, imageSrc: "", title: "", onCrop: null });
                                }
                            });
                        } else if (aadharBackPasteAreaRef.current?.contains(active)) {
                            setCropState({
                                isOpen: true,
                                imageSrc: reader.result,
                                title: "Crop Aadhar Card Back",
                                onCrop: (croppedBlob) => {
                                    const croppedFile = new File([croppedBlob], "aadhar_back.jpg", { type: "image/jpeg" });
                                    setAadharBackFile(croppedFile);
                                    setPreviewAadharBack(URL.createObjectURL(croppedBlob));
                                    setErrors(prev => ({ ...prev, aadharFile: '' }));
                                    setCropState({ isOpen: false, imageSrc: "", title: "", onCrop: null });
                                }
                            });
                        }
                    };
                    reader.readAsDataURL(blob);
                    break;
                }
            }
        };

        window.addEventListener("paste", handlePaste);
        return () => window.removeEventListener("paste", handlePaste);
    }, []);


    const [errors, setErrors] = React.useState({
        signatureFile: '',
        aadharFile: '',
    });
    const excludedFields = [
        "recordstatusid",
        "createdby",
        "createddate",
        "modifiedby",
        "modifieddate",
        "timestamp",
        "id"
    ];
    const handleChange = (e) => {
        let { name, value } = e.target;
        if (name === "Mobile") {
            value = value.replace(/\D/g, '').substring(0, 10);
        }
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        if (!aadharFrontFile && !aadharBackFile) {
            setAadharFile(null);
            setPreviewAadhar("");
            return;
        }

        if (aadharFrontFile && !aadharBackFile) {
            setAadharFile(aadharFrontFile);
            setPreviewAadhar(previewAadharFront);
            return;
        }

        if (!aadharFrontFile && aadharBackFile) {
            setAadharFile(aadharBackFile);
            setPreviewAadhar(previewAadharBack);
            return;
        }

        const imgFront = new Image();
        const imgBack = new Image();

        let loadedCount = 0;
        const onImagesLoaded = () => {
            loadedCount++;
            if (loadedCount === 2) {
                const canvas = document.createElement("canvas");
                canvas.width = imgFront.naturalWidth + imgBack.naturalWidth;
                canvas.height = Math.max(imgFront.naturalHeight, imgBack.naturalHeight);

                const ctx = canvas.getContext("2d");
                ctx.fillStyle = "#FFFFFF";
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                ctx.drawImage(imgFront, 0, 0);
                ctx.drawImage(imgBack, imgFront.naturalWidth, 0);

                canvas.toBlob((blob) => {
                    if (blob) {
                        const mergedFile = new File([blob], "aadhar_merged.jpg", { type: "image/jpeg" });
                        setAadharFile(mergedFile);
                        setPreviewAadhar(URL.createObjectURL(blob));
                    }
                }, "image/jpeg", 0.9);
            }
        };

        imgFront.onload = onImagesLoaded;
        imgBack.onload = onImagesLoaded;

        imgFront.src = previewAadharFront;
        imgBack.src = previewAadharBack;

    }, [aadharFrontFile, aadharBackFile, previewAadharFront, previewAadharBack]);

    const handleFileChange = (setter, previewSetter, errorKey, fieldLabel) => (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setCropState({
                    isOpen: true,
                    imageSrc: reader.result,
                    title: `Crop ${fieldLabel}`,
                    onCrop: (croppedBlob) => {
                        const croppedFile = new File([croppedBlob], file.name, { type: "image/jpeg" });
                        setter(croppedFile);
                        previewSetter(URL.createObjectURL(croppedBlob));
                        setErrors((prev) => ({ ...prev, [errorKey]: '' }));
                        setCropState({ isOpen: false, imageSrc: "", title: "", onCrop: null });
                    }
                });
            };
            reader.readAsDataURL(file);
        }
    };


    function FileUpload({ label, onChange, preview, error, pasteRef }) {
        const inputRef = useRef(null);

        return (
            <div className="flex flex-col">
                <label className="mb-1 text-sm font-medium text-gray-700">{label}</label>

                <div
                    ref={pasteRef}
                    tabIndex={0}
                    className="h-32 border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center text-center text-gray-500 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={() => inputRef.current?.click()}
                >
                    <div className="pointer-events-none select-none">
                        📱 Tap to upload or long-press to paste image on tablet<br />
                        💻 Or paste (Ctrl+V) on desktop
                    </div>
                    <span
                        contentEditable
                        suppressContentEditableWarning
                        className="absolute opacity-0 w-0 h-0"
                    ></span>
                </div>


                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    onChange={onChange}
                    hidden
                />

                {error && <p className="text-red-500 text-sm mt-1">{error}</p>}

                {preview && (
                    <img src={preview} alt="Preview" className="mt-2 max-w-xs max-h-32 border rounded" />
                )}
            </div>
        );
    }


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (form.Mobile && form.Mobile.length !== 10) {
            toast.error("Mobile number must be exactly 10 digits");
            return;
        }
        if (form.Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.Email)) {
            toast.error("Please enter a valid email address");
            return;
        }

        setSubmitting(true);
        setSubmitted(false);

        const fixDate = (d) => (d ? new Date(d).toISOString() : "");

        const formData = new FormData();
        Object.entries(form).forEach(([key, value]) => {
            formData.append(key, key.toLowerCase().includes("date") ? fixDate(value) : value);
        });

        if (signatureFile) formData.append("SignatureFile", signatureFile);
        if (aadharFile) formData.append("AadharCardFile", aadharFile);

        try {
            if (isEditMode) {
                // await updateConsumer(initialData.id, formData);
                // toast.success("Consumer record updated successfully");
                try {
                    const response = await updateConsumer(initialData.id, formData);
                    if (response?.success) {
                        toast.success(response.message);
                    } else {
                        toast.error(response?.message || "Updation failed");
                    }
                } catch (error) {
                    toast.error(error.response?.message || "An error occurred during updation");
                    console.error("Updation error:", error);
                }



            } else {
                let valid = true;
                const newErrors = { signatureFile: '', aadharFile: '' };
                if (!signatureFile) {
                    newErrors.signatureFile = 'Please upload signature';
                    valid = false;
                }

                // if (!aadharFile) {
                //     newErrors.aadharFile = 'Please upload Aadhar card';
                //     valid = false;
                // }
                setErrors(newErrors);
                if (!valid) return;

                // await registerConsumer(formData);
                // toast.success("Consumer record saved successfully");
                try {
                    const response = await registerConsumer(formData);
                    if (response?.success) {
                        toast.success(response.message);
                    } else {
                        toast.error(response?.message || "Registration failed");
                    }
                } catch (error) {
                    toast.error(error.response?.message || "An error occurred during registration.");
                    console.error("Registration error:", error);
                }
            }
            setSubmitted(true);
            onClose();
        } catch (err) {
            console.error(err);
            toast.error("Failed to save consumer record");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="p-4 sm:p-8 max-w-5xl mx-auto bg-white shadow-2xl rounded-2xl space-y-8"
        >
            <div className="border-b pb-4">
                <h2 className="text-2xl font-semibold text-gray-800">
                    {isEditMode ? "Edit Consumer Record" : "Create Consumer Record"}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                    Please fill in all the fields carefully.
                </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {Object.entries(form)
                    .filter(([key]) => !excludedFields.includes(key.toLowerCase()))
                    .map(([key, value]) => {
                        const label = key
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (str) => str.toUpperCase());
                        const isDate = key.toLowerCase().includes("date");
                        if (!isEditMode && ["signaturepath", "aadharcardpath"].includes(key.toLowerCase())) {
                            return null;
                        }
                        if (isEditMode && key.toLowerCase().includes("path") && value) {
                            return (
                                <div key={key} className="flex flex-col w-[250px]">
                                    <label className="mb-1 text-sm font-medium text-gray-700">{label}</label>
                                    <a
                                        href={`${value}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-blue-600 underline"
                                    >
                                        View File
                                    </a>
                                </div>
                            );
                        }
                        if (key.toLowerCase() === "category") {
                            return (
                                <div key={key} className="flex flex-col">
                                    <label htmlFor={key} className="mb-1 text-sm font-medium text-gray-700">
                                        {label}
                                    </label>
                                    <select
                                        id={key}
                                        name={key}
                                        value={value || ""}
                                        onChange={handleChange}
                                        required={requiredFields.includes(key)}
                                        className="px-2 py-2 rounded-lg border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800"
                                    >
                                        <option value="">-- Select Category --</option>
                                        <option value="Private Sector">Private Sector</option>
                                        <option value="Government Sector">Government Sector</option>
                                    </select>
                                </div>
                            );
                        }

                        return (
                            <div key={key} className="flex flex-col">
                                <label
                                    htmlFor={key}
                                    className="mb-1 text-sm font-medium text-gray-700"
                                >
                                    {label}
                                </label>
                                <input
                                    type={isDate ? "date" : ["capacity", "wattage", "count", "rating"].some(str => key.toLowerCase().includes(str)) ? "number" : "text"}
                                    id={key}
                                    name={key}
                                    value={isDate ? (value ? value.slice(0, 10) : "") : value}
                                    onChange={handleChange}
                                    step={["capacity", "rating"].some(str => key.toLowerCase().includes(str)) ? "0.01" : undefined}
                                    required={requiredFields.includes(key)}
                                    className="px-2 py-2 rounded-lg border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800"
                                    placeholder={label}
                                />

                            </div>
                        );
                    })}
            </div>
            <div className="flex flex-col gap-4 border p-4 rounded-xl bg-slate-50/50">
                <span className="text-sm font-semibold text-gray-700">Aadhar Card Documents</span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FileUpload
                        label="Aadhar Card Front"
                        onChange={handleFileChange(setAadharFrontFile, setPreviewAadharFront, 'aadharFile', 'Aadhar Card Front')}
                        preview={previewAadharFront}
                        error={errors.aadharFile}
                        pasteRef={aadharFrontPasteAreaRef}
                    />
                    <FileUpload
                        label="Aadhar Card Back"
                        onChange={handleFileChange(setAadharBackFile, setPreviewAadharBack, 'aadharFile', 'Aadhar Card Back')}
                        preview={previewAadharBack}
                        error={errors.aadharFile}
                        pasteRef={aadharBackPasteAreaRef}
                    />
                </div>
                {previewAadhar && (
                    <div className="mt-2 border-t pt-2">
                        <span className="text-xs font-semibold text-gray-500 block mb-1">Merged Horizontal Preview (To Upload)</span>
                        <img src={previewAadhar} alt="Aadhar Merged Preview" className="max-h-32 border rounded object-contain bg-white" />
                    </div>
                )}
            </div>
            <div className="flex flex-col gap-4 border p-4 rounded-xl bg-slate-50/50">
                <div className="flex justify-between items-center border-b pb-2">
                    <span className="text-sm font-semibold text-gray-700">Signature Verification</span>
                    <div className="flex bg-gray-150 p-1 rounded-lg border text-xs font-semibold gap-1">
                        <button
                            type="button"
                            onClick={() => {
                                setSignatureMode("upload");
                                setSignatureFile(null);
                                setPreviewSignature("");
                            }}
                            className={`px-3 py-1 rounded-md transition ${signatureMode === "upload" ? "bg-white text-gray-800 shadow" : "text-gray-500 hover:text-gray-800"}`}
                        >
                            Upload File
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setSignatureMode("draw");
                                setSignatureFile(null);
                                setPreviewSignature("");
                            }}
                            className={`px-3 py-1 rounded-md transition ${signatureMode === "draw" ? "bg-white text-gray-800 shadow" : "text-gray-500 hover:text-gray-800"}`}
                        >
                            Draw Manually
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-start">
                    {signatureMode === "upload" ? (
                        <FileUpload
                            label="Upload Signature Image"
                            onChange={handleFileChange(setSignatureFile, setPreviewSignature, 'signatureFile', 'Signature')}
                            preview={previewSignature}
                            error={errors.signatureFile}
                            pasteRef={signaturePasteAreaRef}
                        />
                    ) : (
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-medium text-gray-700">Draw Signature (Use finger, stylus, or mouse)</label>
                            <SignatureDrawingPad
                                onSave={(blob) => {
                                    const drawnFile = new File([blob], "signature_draw.png", { type: "image/png" });
                                    setSignatureFile(drawnFile);
                                    setPreviewSignature(URL.createObjectURL(blob));
                                    setErrors((prev) => ({ ...prev, signatureFile: '' }));
                                }}
                                onClearActive={() => {
                                    setSignatureFile(null);
                                    setPreviewSignature("");
                                }}
                            />
                            {errors.signatureFile && <p className="text-red-500 text-sm mt-1">{errors.signatureFile}</p>}
                        </div>
                    )}

                    {previewSignature && signatureMode === "draw" && (
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-medium text-gray-700">Signature Preview</label>
                            <div className="h-36 border rounded-lg bg-white flex items-center justify-center p-2">
                                <img src={previewSignature} alt="Signature Preview" className="max-h-full max-w-full object-contain border" />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex gap-4 mt-6 justify-center items-center">
                {/* Submit/Update Button */}
                <button
                    type="submit"
                    className="bg-blue-600 hover:bg-sky-700 text-white font-medium px-5 py-2 text-sm rounded-md shadow-sm transition"
                    disabled={submitting}
                >
                    {submitting ? (isEditMode ? "Updating..." : "Saving...") : (isEditMode ? "Update" : "Submit")}
                </button>

                {/* Cancel Button */}
                <button
                    type="button"
                    onClick={onClose}
                    className="bg-gray-200 hover:bg-gray-300 text-black font-medium px-5 py-2 text-sm rounded-md shadow-sm transition"
                >
                    Cancel
                </button>

                {/* Success Message */}
                {submitted && (
                    <div className="text-green-600 text-sm font-medium sm:ml-4">
                        ✅ Consumer record {isEditMode ? "updated" : "saved"} successfully.
                    </div>
                )}
            </div>

            {cropState.isOpen && (
                <ImageCropperModal
                    imageSrc={cropState.imageSrc}
                    title={cropState.title}
                    onCrop={cropState.onCrop}
                    onCancel={() => setCropState({ isOpen: false, imageSrc: "", title: "", onCrop: null })}
                />
            )}
        </form>
    );
};

export default ConsumerForm;
